/** Utilities for traversing fields and translating rendered values to the legacy submission shape. */
import type { DynamicFormDefinition, FormField, FormRow, FormValues, LegacyPostedElement } from '../models/form';
import { normalizeNumericText } from './numbers';
import { evaluateCalculation } from './calculation';

function flattenFields(fields: FormField[]): FormField[] {
  const output: FormField[] = [];
  for (const field of fields) {
    output.push(field);
    if (field.children) output.push(...flattenFields(field.children));
    if (field.panelRows?.length) output.push(...flattenRows(field.panelRows));
    // Table columns belong to each table row and are not top-level form models.
    // They are validated and rendered by TableField instead of being inserted in page values.
  }
  return output;
}

function flattenRows(rows: FormRow[]): FormField[] {
  const output: FormField[] = [];
  for (const row of rows) {
    for (const column of row.columns) {
      output.push(...flattenFields(column.fields));
      if (column.rows?.length) output.push(...flattenRows(column.rows));
    }
  }
  return output;
}

/** Returns all fields in a tab, including nested layouts, panels and table columns. */
export function getTabFields(form: DynamicFormDefinition, tabName: string): FormField[] {
  const tab = form.Tabs.find((item) => item.name === tabName);
  if (!tab) return [];
  return flattenRows(tab.rows);
}

/** Flattens every field in every tab for validation and binding lookups. */
export function getAllFields(form: DynamicFormDefinition): FormField[] {
  return form.Tabs.flatMap((tab) => getTabFields(form, tab.name));
}

/** Rebuilds DF_FormElements metadata expected by reports and existing backend queries. */
export function buildLegacyElements(form: DynamicFormDefinition, tabName: string): LegacyPostedElement[] {
  const unique = new Map<string, LegacyPostedElement>();
  for (const field of getTabFields(form, tabName)) {
    if (!isDataField(field)) continue;
    const item: LegacyPostedElement = {
      Name: field.name,
      Title: field.designerTitle || field.title || field.name,
      Model: field.name,
      FullModel: `${form.Name}.${tabName}.${field.name}`,
      Tag: field.type
    };
    unique.set(field.name, item);
  }
  return [...unique.values()];
}

export function isDataField(field: FormField): boolean {
  return ['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload', 'table', 'simplecaptcha', 'captcha'].includes(field.type);
}

/** Creates the nested initial-value object from field defaults. */
export function createInitialValues(form: DynamicFormDefinition): FormValues {
  const values: FormValues = {};
  for (const tab of form.Tabs) {
    values[tab.name] = {};
    for (const field of getTabFields(form, tab.name)) {
      if (!isDataField(field)) continue;
      values[tab.name]![field.name] = field.type === 'table'
        ? []
        : field.type === 'checkbox'
          ? field.defaultValue === true || String(field.defaultValue ?? '').toLowerCase() === 'true'
          : field.defaultValue ?? '';
    }
  }
  return values;
}

export function mergeValues(base: FormValues, loaded: unknown): FormValues {
  if (!loaded || typeof loaded !== 'object') return base;
  const result: FormValues = structuredClone(base);
  for (const [pageName, pageValue] of Object.entries(loaded as Record<string, unknown>)) {
    if (!pageValue || typeof pageValue !== 'object') continue;
    result[pageName] = { ...(result[pageName] ?? {}), ...(pageValue as Record<string, unknown>) };
  }
  return result;
}

function collectTableValueFields(field: FormField): FormField[] {
  const allowed = new Set<FormField['type']>(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload']);
  const output: FormField[] = [...(field.tableColumns ?? []).filter((item) => allowed.has(item.type))];
  const seen = new Set(output.map((item) => item.name || item.id));
  const visitFields = (fields: FormField[]) => {
    for (const item of fields) {
      if (allowed.has(item.type)) {
        const key = item.name || item.id;
        if (!seen.has(key)) { seen.add(key); output.push(item); }
      }
      if (item.children?.length) visitFields(item.children);
      if (item.panelRows?.length) visitRows(item.panelRows);
    }
  };
  const visitRows = (rows: FormRow[]) => {
    for (const row of rows) for (const column of row.columns) {
      visitFields(column.fields);
      if (column.rows?.length) visitRows(column.rows);
    }
  };
  if (field.tableRows?.length) visitRows(field.tableRows);
  return output;
}

function normalizeFieldValue(field: FormField, value: unknown): unknown {
  if (field.type === 'input' && field.inputType === 'number') {
    return value == null || String(value).trim() === '' ? value : normalizeNumericText(value);
  }
  if (field.type === 'table' && Array.isArray(value)) {
    return value.map((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
      const normalized = { ...(row as Record<string, unknown>) };
      for (const column of collectTableValueFields(field)) {
        if (Object.prototype.hasOwnProperty.call(normalized, column.name)) {
          normalized[column.name] = normalizeFieldValue(column, normalized[column.name]);
        }
      }
      return normalized;
    });
  }
  return value;
}

/** Normalizes values that have a stable wire representation before posting to the legacy backend. */
/** Removes display-only values and normalizes data before JSON submission. */
export function normalizeValuesForSubmit(form: DynamicFormDefinition, values: FormValues): FormValues {
  const result: FormValues = structuredClone(values);
  for (const tab of form.Tabs) {
    const page = result[tab.name];
    if (!page) continue;
    for (const field of getTabFields(form, tab.name)) {
      if (!Object.prototype.hasOwnProperty.call(page, field.name)) continue;
      page[field.name] = normalizeFieldValue(field, page[field.name]);
    }
  }
  return result;
}


/** Recalculates all read-only arithmetic fields before validation and submission. */
export function applyCalculatedValues(form: DynamicFormDefinition, values: FormValues): FormValues {
  const result: FormValues = structuredClone(values);
  // Multiple bounded passes resolve formulas that depend on another calculated field without using eval.
  const calculatedCount = form.Tabs.reduce((count, tab) => count + getTabFields(form, tab.name).filter((field) => field.type === 'input' && field.calculationFormula !== undefined).length, 0);
  for (let pass = 0; pass < Math.max(1, calculatedCount + 1); pass += 1) {
    let changed = false;
    for (const tab of form.Tabs) {
      const page = result[tab.name] ?? (result[tab.name] = {});
      for (const field of getTabFields(form, tab.name)) {
        if (field.type !== 'input' || field.calculationFormula === undefined || !field.name) continue;
        let number = 0;
        try { number = evaluateCalculation(field.calculationFormula, result, tab.name); }
        catch { number = 0; }
        const decimals = Math.max(0, Math.min(8, Math.trunc(field.calculationDecimals ?? 0)));
        const next = number.toFixed(decimals);
        if (String(page[field.name] ?? '') !== next) { page[field.name] = next; changed = true; }
      }
    }
    if (!changed) break;
  }
  return result;
}
